import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';

import {
  CheckboxTogglePage,
  ClearAllSelectedVehiclesUuid,
  ConfigurationState,
  DateUtilsService,
  InventoryManagementAuctionItemStatus,
  InventoryManagementDateRange,
  InventoryManagementSearchRequest,
  InventoryManagementSearchResult,
  InventoryManagementState,
  InventoryManagementValidateProcessingVehicles,
  LaunchDarklyService,
  SearchCriteria,
  SearchService,
  SetCurrentVehiclePage,
  SetSelectedTenant,
  StatusFilter,
  TenantFilter,
  ToggleStateService,
  UpdateInventoryManagementListViewToggle,
  UpdateSearchRequest,
  UpdateSearchResponse,
  UpdateShouldMaintainPreviousSearchInStateConfiguration,
  UpdateVehicleSelectAll,
  UserProfileState,
  VehicleToggleState
} from 'remarketing-angular-library';
import {
  SidebarComponent
} from '../../../sell/lib/manage-inventory/components/sidebar/sidebar.component';
import { select, Store } from '@ngxs/store';
import { saveAs } from 'file-saver';
import {
  SwVehicleContainerHookComponent
} from '../../../shared/components/stockwave/sw-vehicle-container-hook/sw-vehicle-container-hook.component';
import { SearchFilterComponent } from '../search-filter/search-filter.component';
import { catchError, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  AnalyticsService,
  fromMatchMediaSignal,
  SaComponent
} from 'smartauction-frontend-micro-library';
import {
  ActionSubmittedModalComponent,
  ActionSubmittingModalComponent,
  CommonBannerComponent,
  ErrorMessageComponent,
  PaginationComponent
} from 'remarketing-ui-components-lib';
import { translateObjectSignal, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  ActiveVehiclesCountComponent
} from '../active-vehicles-count/active-vehicles-count.component';
import { UtilityBarComponent } from '../utility-bar/utility-bar.component';
import { VehicleCardComponent } from '../vehicle-card/vehicle-card.component';
import { PaginationConfig } from 'remarketing-angular-library/models/pagination-config.model';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { chunkArrayBySize } from '../../../app/shared/utils/util';
import { BehaviorSubject, EMPTY, of } from 'rxjs';
import { InventorySkeletonComponent } from '../inventory-skeleton/inventory-skeleton.component';
import { CommonModule } from '@angular/common';
import { MultiVinErrorComponent } from '../multi-vin-error/multi-vin-error.component';
import {
  InventoryManagementHeaderComponent
} from '../inventory-management-header/inventory-management-header.component';
import {
  SearchFilterParametersService
} from '../../services/search-filter-parameters/search-filter-parameters.service';
import {
  AccountManagerService
} from '../../services/account-manager/account-manager.service';
import {
  ListViewInventoryItemComponent
} from '../list-view-inventory-item/list-view-inventory-item.component';
import { ActionListComponent } from '../action-list/action-list.component';
import { VehicleToggleService } from '../../services/vehicle-toggle/vehicle-toggle.service';
import {
  InventoryManagementPaginatedSearchResponse
} from 'remarketing-angular-library/models/inventory-management/inventory-management-search-response.model';
import {
  SharedListMultiActionModalComponent
} from '../../../shared/components/offer-modals/shared-list-multi-action-modal/shared-list-multi-action-modal.component';
import { MultiOfferActionBannerInfo } from '@ally/remarketing/shared/services';

@Component({
  selector: 'app-inventory-management-layout',
  imports: [
    ActiveVehiclesCountComponent,
    SwVehicleContainerHookComponent,
    VehicleCardComponent,
    SearchFilterComponent,
    ActiveVehiclesCountComponent,
    ActiveVehiclesCountComponent,
    ErrorMessageComponent,
    InventorySkeletonComponent,
    TranslocoDirective,
    UtilityBarComponent,
    ActiveVehiclesCountComponent,
    ActiveVehiclesCountComponent,
    UtilityBarComponent,
    VehicleCardComponent,
    PaginationComponent,
    ActionSubmittingModalComponent,
    ActionSubmittedModalComponent,
    ErrorModalComponent,
    InventoryManagementHeaderComponent,
    MultiVinErrorComponent,
    CommonModule,
    ListViewInventoryItemComponent,
    ActionListComponent,
    ActionListComponent,
    SharedListMultiActionModalComponent,
    CommonBannerComponent
  ],
  providers: [
    ToggleStateService,
    SearchFilterParametersService,
    AccountManagerService
  ],
  templateUrl: './inventory-management-layout.component.html',
  styleUrl: './inventory-management-layout.component.scss'
})
export class InventoryManagementLayoutComponent extends SaComponent implements OnInit {
  protected readonly sidebar$ = viewChild(SidebarComponent);
  protected readonly actionSubmittingModal$ = viewChild(ActionSubmittingModalComponent);
  protected readonly actionSubmittedModal$ = viewChild(ActionSubmittedModalComponent);
  protected readonly utilityBar$ = viewChild(UtilityBarComponent);
  protected readonly sharedListMultiActionModal$ = viewChild(SharedListMultiActionModalComponent);

  private readonly searchService: SearchService;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly store: Store;
  private readonly translocoService: TranslocoService;
  private readonly analyticsService: AnalyticsService;
  private readonly searchFilterParametersService: SearchFilterParametersService;
  private readonly changeDetectorRef: ChangeDetectorRef;
  private readonly vehicleToggleService: VehicleToggleService;
  private readonly dateUtilsService: DateUtilsService;

  protected readonly inventorySearchCriteria$ = select(InventoryManagementState.searchCriteria);
  protected readonly inventorySearchResponseVehicles$ = select(InventoryManagementState.searchResponseVehicles);
  protected readonly inventorySearchResponse$ = select(InventoryManagementState.searchResponse);
  protected readonly inventorySearchRequest$ = select(InventoryManagementState.searchRequest);
  protected readonly inventoryCurrentStatus$ = select(InventoryManagementState.currentStatus);
  protected readonly isListView$ = select(InventoryManagementState.isListViewToggled);
  protected readonly configuration$ = select(ConfigurationState.configuration);
  protected readonly sellConfiguration$ = select(ConfigurationState.coreComponentConfig('sell'));
  protected readonly userProfile$ = select(UserProfileState.userProfile);
  protected readonly isMobileLayout$ = fromMatchMediaSignal('(max-width: 767px)');
  protected readonly filterByIsv$ = select(InventoryManagementState.filterByInService);
  protected readonly selectedVehicles$ = select(VehicleToggleState.selectedVehicles);
  protected readonly sellVehiclesToggled$ = computed(() => {
    const sellVehicles = this.selectedVehicles$();
    return sellVehicles as InventoryManagementSearchResult[];
  });

  protected readonly translations$ = translateObjectSignal(['UIComponents', 'sellModule.manageInventory']);
  protected readonly inventoryManagementLayoutTranslations$ = computed(() => {
    const translations = this.translations$();
    const vehicleResultTextMap = {
      [StatusFilter.ACTIVE_VEHICLES]: translations[1]?.vehicleTextTranslations?.textActive,
      [StatusFilter.RESERVED_VEHICLES]: translations[1]?.vehicleTextTranslations?.textReserved,
      [StatusFilter.SOLD_VEHICLES]: translations[1]?.vehicleTextTranslations?.textSold,
      [StatusFilter.HOLD_VEHICLES]: translations[1]?.vehicleTextTranslations?.textHold,
      [StatusFilter.REMOVED_VEHICLES]: translations[1]?.vehicleTextTranslations?.textRemoved,
      [StatusFilter.SHARED_VEHICLES]:
        this.isInServiceVehiclesEnabled && !this.isVehicleFilterTypeISV
          ? translations[1]?.vehicleTextTranslations?.textCustomOffer
          : translations[1]?.vehicleTextTranslations.textShared,
      [StatusFilter.ALL_VEHICLES]: this.getAllVehiclesResultText(translations)
    };
    return {
      vehicleResultText: vehicleResultTextMap[this.inventoryCurrentStatus$()],
      auctionSubmitted: {
        textClose: translations[0]?.auctionSubmitted?.textClose,
        ariaLabelClose: translations[0]?.auctionSubmitted?.ariaLabelClose
      },
      paginationTranslation: {
        textResultsPerPage: translations[0]?.pagination?.textResultsPerPage
      },
      textSold: translations[1]?.layout?.textSold,
      textRemoved: translations[1]?.layout?.textRemoved,
      labelErrorTitle: translations[1]?.layout?.labelErrorTitle,
      labelErrorSubTitle: translations[1]?.layout?.labelErrorSubTitle,
      historicalHigh: translations[1]?.vehicleCard?.historicalHigh
    };
  });

  protected readonly pagination$ = computed(() => {
    const searchResponse = this.inventorySearchResponse$();
    return {
      itemsPerPage: searchResponse?.itemsPerPage || 10,
      page: searchResponse?.page || 0,
      totalItems: searchResponse?.totalItems || 0,
      totalPages: searchResponse?.totalPages || 0
    } as PaginationConfig;
  });

  protected readonly selectedVehicleStatus$ = computed(() => {
    const selectedVehicles = this.selectedVehicles$() as InventoryManagementSearchResult[];

    const filterByStatusMap = {
      [InventoryManagementAuctionItemStatus.ACTIVE]: StatusFilter.ACTIVE_VEHICLES,
      [InventoryManagementAuctionItemStatus.SOLD]: StatusFilter.SOLD_VEHICLES,
      [InventoryManagementAuctionItemStatus.HOLD]: StatusFilter.HOLD_VEHICLES,
      [InventoryManagementAuctionItemStatus.REMOVED]: StatusFilter.REMOVED_VEHICLES,
      [InventoryManagementAuctionItemStatus.RESERVED]: StatusFilter.RESERVED_VEHICLES
    };

    return selectedVehicles.length > 0 ? filterByStatusMap[selectedVehicles[0]?.auctionItemStatus] : undefined;
  });

  protected readonly inventoryManagementVehicles$ = computed(() => {
    const vehicles = this.inventorySearchResponseVehicles$();
    const hadMultiOfferSuccess = this.hadMultiOfferActionSuccess$();

    if (vehicles) {
      return vehicles.map(vehicle => {
        if (hadMultiOfferSuccess.has(vehicle.vehicleUuid)) {
          return {
            ...vehicle,
            saleDate: this.dateUtilsService.localizeDateAndTime(Date.now()),
            auctionItemStatus: hadMultiOfferSuccess.get(vehicle.vehicleUuid)
          };
        }
        return vehicle;
      });
    }

    return vehicles;
  });

  protected leadSellerRequestMade$: BehaviorSubject<boolean>;
  protected isLoading = true;
  protected searchError: any = undefined;
  protected isExporting = false;
  protected hasExportActionError = false;
  protected exportActionText: string;
  protected exportActionSuccessText = '';
  protected mobileFilter = false;
  protected disableSelectAll = false;
  protected userType = 'SELLER';
  protected multiOfferActionSuccess = null;
  protected multiOfferActionErrors = [];
  private hadMultiOfferActionSuccess$: WritableSignal<Map<string, InventoryManagementAuctionItemStatus>> = signal(
    new Map<string, InventoryManagementAuctionItemStatus>()
  );

  constructor() {
    super();
    this.searchService = inject(SearchService);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.store = inject(Store);
    this.translocoService = inject(TranslocoService);
    this.changeDetectorRef = inject(ChangeDetectorRef);
    this.searchFilterParametersService = inject(SearchFilterParametersService);
    this.vehicleToggleService = inject(VehicleToggleService);
    this.dateUtilsService = inject(DateUtilsService);
    this.analyticsService = inject(AnalyticsService);
    /*
      WARNING: Reading a signal inside this effect could cause an infinite search call loop!!!
      If you must, ensure that the signal NEVER updates while the Sell Search Page is open!!!
    */
    effect(() => {
      if (this.searchFilterParametersService.areParamsComplete()) {
        this.performInventoryManagementSearch();
      }
    });
  }

  trackVehicleById(index: number, result: InventoryManagementSearchResult) {
    return result.auctionItemUuid;
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get areSellCheckboxesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-manageInventorySelectionEnabled', false);
  }

  get sellerCommentsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-sellerCommentsEnabled', false);
  }

  get isSellReportingFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-saleReportsEnabled', false);
  }

  get isListViewDefault(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-manage-inventory-default-cardview', false);
  }

  get isListViewFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-listViewEnabled', true);
  }

  get isHistoricalHighEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-historicalHighEnabled', false);
  }

  get showHistoricalHighColumn(): boolean {
    const currentStatus = this.inventoryCurrentStatus$();
    const isActiveOrSharedStatus =
      currentStatus === StatusFilter.ACTIVE_VEHICLES ||
      currentStatus === StatusFilter.SHARED_VEHICLES;
    return this.isHistoricalHighEnabled && isActiveOrSharedStatus;
  }

  get filterStatusName() {
    return this.isInServiceVehiclesEnabled && this.inventoryCurrentStatus$() === StatusFilter.SHARED_VEHICLES
      ? StatusFilter.INVOICED_VEHICLES
      : this.inventoryCurrentStatus$();
  }

  get isMobile() {
    return this.isMobileLayout$().matches;
  }

  get isServiceFeeVisible() {
    return !!this.sellConfiguration$()?.fields['service-fee']?.visible;
  }

  hadMultiOfferActionSuccess(inventoryManagementSearchResult: InventoryManagementSearchResult): boolean {
    return this.hadMultiOfferActionSuccess$().has(inventoryManagementSearchResult.vehicleUuid);
  }

  get isSingleOfferListSelected(): boolean {
    return this.inventorySearchCriteria$()?.offerLists?.length === 1;
  }

  get InventorySearchResponseCount() {
    return this.inventorySearchResponse$()?.totalItems && !this.isLoading && !this.searchError
      ? this.inventorySearchResponse$().totalItems
      : undefined;
  }

  get missingVins(): string[] {
    const missingVins = this.inventorySearchResponse$()?.metadata?.missingVins;
    return this.isMissingVinsOrStockNumbers && missingVins?.length > 0 ? missingVins : [];
  }

  get missingStockNumbers(): string[] {
    const missingStocks = this.inventorySearchResponse$()?.metadata?.missingStockNumbers;
    return this.isMissingVinsOrStockNumbers && missingStocks?.length > 0 ? missingStocks : [];
  }

  get isMissingVinsOrStockNumbers(): boolean {
    const metaData = this.inventorySearchResponse$()?.metadata;
    return metaData && (!!metaData?.missingVins?.length || !!metaData?.missingStockNumbers?.length);
  }

  get isSellerBulkSaleFeature(): boolean {
    return (
      this.launchDarklyService.getFeatureFlag('directSales-seller-enabled', false) &&
      this.launchDarklyService.getFeatureFlag('directSales-seller-createEnabled', false)
    );
  }

  get isListViewToggleVisible(): boolean {
    return !this.isSellerBulkSaleFeature || this.inventoryCurrentStatus$() !== StatusFilter.ACTIVE_VEHICLES;
  }

  updateListViewToggle(isListView: boolean) {
    this.store.dispatch(new UpdateInventoryManagementListViewToggle(isListView));
  }

  get showSearchTip() {
    let searchTip: string;
    const request = this.store.selectSnapshot(InventoryManagementState.searchCriteria);
    const statusFilter = request.filterByStatus;
    switch (statusFilter) {
      case InventoryManagementAuctionItemStatus.SOLD:
        searchTip = this.inventoryManagementLayoutTranslations$().textSold;
        break;
      case InventoryManagementAuctionItemStatus.REMOVED:
        searchTip = this.inventoryManagementLayoutTranslations$().textRemoved;
        break;
      default:
        searchTip = '';
        break;
    }
    return searchTip;
  }

  get isVehicleFilterTypeISV() {
    if (this.filterByIsv$()) {
      return (
        this.inventoryCurrentStatus$() === StatusFilter.RESERVED_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.ACTIVE_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.REMOVED_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.ALL_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.HOLD_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.SHARED_VEHICLES ||
        this.inventoryCurrentStatus$() === StatusFilter.SOLD_VEHICLES
      );
    }
  }

  get showSaleFeeAndSalePriceColumn() {
    return (
      this.inventoryCurrentStatus$() === StatusFilter.SHARED_VEHICLES ||
      this.inventoryCurrentStatus$() === StatusFilter.SOLD_VEHICLES
    );
  }

  get showTotalPriceColumn() {
    return this.inventoryCurrentStatus$() === StatusFilter.SHARED_VEHICLES && this.isSingleOfferListSelected;
  }

  get tenantName() {
    return this.configuration$()?.tenantName;
  }

  get tenantUuid() {
    return this.configuration$()?.tenantUuid;
  }

  get areMultipleTenantsSelected(): boolean {
    const initialTenantUuid = this.sellVehiclesToggled$()[0]?.auctionItemTenantUuid;
    return (
      this.sellVehiclesToggled$().length > 1 &&
      this.sellVehiclesToggled$().some(vehicle => vehicle.auctionItemTenantUuid !== initialTenantUuid)
    );
  }

  ngOnInit() {
    this.searchFilterParametersService.handleInitialSearch();
    this.setListView();
    this.resetCheckboxForPage();
  }

  setListView(): void {
    const maintainPreviousSearch = this.store.selectSnapshot(
      InventoryManagementState.shouldMaintainPreviousSearchInState
    );
    if (this.isListViewDefault && !maintainPreviousSearch) {
      this.store.dispatch(new UpdateInventoryManagementListViewToggle(true));
    }
  }

  resetCheckboxForPage() {
    this.store.dispatch(new SetCurrentVehiclePage(CheckboxTogglePage.SELL));
  }

  isVehicleToggled(vehicleUuid: string) {
    const selectedVehiclesToggled = this.store.selectSnapshot(VehicleToggleState.selectedVehicles);
    return selectedVehiclesToggled.some(
      (vehicle: InventoryManagementSearchResult) => vehicle.vehicleUuid === vehicleUuid
    );
  }

  /*
    WARNING: This method is inside an effect hook.
    Reading a signal inside this method could cause an infinite search call loop!!!
    If you must, ensure that the signal NEVER updates while the Sell Search Page is open
 */
  performInventoryManagementSearch(): void {
    this.store
      .select(InventoryManagementState.searchRequest)
      .pipe(
        switchMap(searchRequest => {
          this.isLoading = true;
          this.searchError = undefined;
          this.disableSelectAll = false;
          this.clearSelectedVehicles();
          this.store.dispatch(new UpdateShouldMaintainPreviousSearchInStateConfiguration(false));
          this.hadMultiOfferActionSuccess$.update(() => new Map<string, InventoryManagementAuctionItemStatus>());
          return this.searchService.inventoryManagementSearch(this.buildRequestForInventoryManagementSearch(searchRequest)).pipe(
            catchError(error => {
              this.isLoading = false;
              this.searchError = error;
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: response => {
          this.handleInventoryManagementResponseSuccess(response);
        }
      });
  }

  // set the search request's date range to UTC time to sync with back end
  private buildRequestForInventoryManagementSearch(searchRequest: InventoryManagementSearchRequest): InventoryManagementSearchRequest {
    const dateRange = { ...searchRequest?.searchCriteria?.dateRange };

    // Use start-of-day for fromDate and end-of-day for toDate to capture the full local day in UTC
    const { fromDateUtc, toDateUtc } = this.dateUtilsService.convertDateRangeToUtc(
      dateRange?.fromDate,
      dateRange?.toDate
    );
    dateRange.fromDate = fromDateUtc;
    dateRange.toDate = toDateUtc;

    const inventoryManagementSearchRequest = {
      ...searchRequest,
      searchCriteria: {
        ...searchRequest?.searchCriteria,
        dateRange
      }
    } as InventoryManagementSearchRequest;

    return inventoryManagementSearchRequest;
  }

  /*
    WARNING: This method is used inside an effect hook.
    Reading a signal inside this method could cause an infinite search call loop!!!
    If you must, ensure that the signal NEVER updates while the Sell Search Page is open
  */
  handleInventoryManagementResponseSuccess(response: InventoryManagementPaginatedSearchResponse): void {
    this.isLoading = false;
    this.searchError = false;
    this.store.dispatch(new UpdateSearchResponse(response));
    // if our search response includes a list of vehicles, check to see if any of those vehicles have gone through the status change process
    if (response?.data && response?.data.length > 0) {
      this.store.dispatch(new InventoryManagementValidateProcessingVehicles(response.data));
    }
    this.vehicleToggleService.evaluateSelectAllCheckbox();
    // detecting changes as our network call is async. This is to solve an NG0100 and sync it through Angular's change detection cycle.
    this.changeDetectorRef.detectChanges();
  }

  /*
    WARNING: This method is used inside an effect hook.
    Reading a signal inside this method could cause an infinite search call loop!!!
    If you must, ensure that the signal NEVER updates while the Sell Search Page is open
  */
  clearSelectedVehicles() {
    /*
      do not clear the selected vehicles if it's an ALL VEHICLES search
      as we need to run a new search on the auction item status of that selected vehicle
     */

    /*
      check the previous search metadata to see if the status has changed.
      if changed, then clear the selected vehicles.
    */
    const previousSearchFilterByStatus = this.store.selectSnapshot(InventoryManagementState.searchResponse)?.metadata?.searchCriteria?.filterByStatus;
    const newFilterByStatus = this.store.selectSnapshot(InventoryManagementState.searchCriteria)?.filterByStatus;
    const inventoryCurrentStatus = this.store.selectSnapshot(InventoryManagementState.currentStatus);
    if (
      (previousSearchFilterByStatus && newFilterByStatus && previousSearchFilterByStatus !== newFilterByStatus) &&
      (inventoryCurrentStatus !== StatusFilter.ALL_VEHICLES)
    ) {
      this.store.dispatch(new ClearAllSelectedVehiclesUuid());
    }
  }

  dataTrackScrollNumber(index: number) {
    if (index % 10 === 0) {
      return index;
    }
    return null;
  }

  handleChangePage(page: number) {
    this.store.dispatch(
      new UpdateSearchRequest({
        ...this.inventorySearchRequest$(),
        page: page - 1
      })
    );
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }

  handleChangePerPage(perPage: number) {
    this.store.dispatch(
      new UpdateSearchRequest({
        ...this.inventorySearchRequest$(),
        page: 0,
        itemsPerPage: perPage
      })
    );
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }

  onExportAll() {
    this.hasExportActionError = false;
    this.isExporting = true;
    this.actionSubmittingModal$().show();

    const exportAllLimit = this.launchDarklyService.getFeatureFlag('sim-exportMaxResults', 3000);

    // Preserve only the date range from the current search criteria and convert to UTC.
    // All other filters (vehicle type, dealer, VIN, etc.) are intentionally excluded.
    const currentDateRange = { ...this.inventorySearchCriteria$()?.dateRange };
    const { fromDateUtc, toDateUtc } = this.dateUtilsService.convertDateRangeToUtc(
      currentDateRange?.fromDate,
      currentDateRange?.toDate
    );

    const request: InventoryManagementSearchRequest = {
      page: 0,
      itemsPerPage: exportAllLimit,
      searchCriteria: {
        filterByStatus: InventoryManagementAuctionItemStatus.SOLD,
        tenantUuid: this.tenantUuid,
        sellExportAllSold: true,
        dateRange: {
          fromDate: fromDateUtc,
          toDate: toDateUtc
        }
      }
    };

    const currentTotalItems = this.inventorySearchResponse$()?.totalItems ?? 0;
    const criteria = this.inventorySearchCriteria$();
    const filterByInService = criteria?.filterByInService;

    const oppositeCount$ = filterByInService !== undefined && filterByInService !== null
      ? this.searchService.inventoryManagementSearch({
          page: 0,
          itemsPerPage: 1,
          searchCriteria: { ...criteria, filterByInService: !filterByInService }
        }).pipe(
          take(1),
          map(resp => currentTotalItems + (resp?.totalItems ?? 0)),
          catchError(() => of(currentTotalItems))
        )
      : of(currentTotalItems);

    oppositeCount$.subscribe(totalItems => {
      this.getExportTextTranslations(totalItems, exportAllLimit);
    });

    this.searchService.exportInventoryManagementAllSoldSearchResults(request)
      .pipe(
        take(1),
        finalize(() => {
          this.isExporting = false;
          this.exportActionText = '';
          this.actionSubmittingModal$().close();
          if (!this.hasExportActionError) {
            this.actionSubmittedModal$().show();
          }
        }))
      .subscribe({
        next: (resp) => {
          const blob = new Blob([resp.body], { type: 'text/csv;charset=utf-8' });
          const localizedDate = this.dateUtilsService.localizeDate(new Date());
          const fileName = 'InventoryExportAll_' + localizedDate + '.csv';
          saveAs(blob, fileName);
        },
        error: error => {
          this.hasExportActionError = true;
          this.actionSubmittingModal$().close();
          console.log(error);
          alert('Failed to export all search results');
        }
      });
  }

  onExport() {
    this.hasExportActionError = false;
    this.isExporting = true;
    this.actionSubmittingModal$().show();
    const request = this.inventorySearchRequest$();
    const response = this.inventorySearchResponse$();
    const sellSearchExportLimit = this.launchDarklyService.getFeatureFlag('sim-exportMaxResults', 3000);

    const { totalItems } = response;
    this.getExportTextTranslations(totalItems, sellSearchExportLimit);

    setTimeout(() => {
      const text = document.getElementsByClassName('action-submitting__tagline');
      if (text.length > 0) {
        const p = text[0].getElementsByTagName('p');
        if (p.length > 0) {
          const sr = document.createElement('span');
          sr.innerText = this.exportActionText;
          sr.className = 'visually-hidden';
          sr.setAttribute('aria-live', 'assertive');
          sr.setAttribute('tabindex', '-1');
          p[0].appendChild(sr);
        }
      }
    }, 100);

    this.searchService.exportInventoryManagementSearchResults(request)
      .pipe(
        take(1),
        finalize(() => {
          this.isExporting = false;
          this.exportActionText = '';
          this.actionSubmittingModal$().close();
          if (!this.hasExportActionError) {
            this.actionSubmittedModal$().show();
          }
        }))
      .subscribe(
        {
          next: (resp) => {
            const blob = new Blob([resp.body], { type: 'text/csv;charset=utf-8' });
            const localizedDate = this.dateUtilsService.localizeDate(new Date());
            const fileName = 'InventoryExport_' + localizedDate + '.csv';
            saveAs(blob, fileName);
          },
          error: error => {
            this.hasExportActionError = true;
            this.actionSubmittingModal$().close();
            console.log(error);
            alert('Failed to export search results');
          }
        }
      );
  }

  getExportTextTranslations(totalItems: number, sellSearchExportLimit: number) {
    const keyParams = {
      'UIComponents.auctionSubmitted.exportActionSearchTextSingular': {
        auctionSummaryExportLimit: sellSearchExportLimit
      },
      'UIComponents.auctionSubmitted.exportActionSearchTextPlural': {
        resultCount: totalItems,
        auctionSummaryExportLimit: sellSearchExportLimit
      },
      'common.labelExportSuccessful': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(take(1))
      .subscribe(translatedObj => {
        this.exportActionText = totalItems === 1 ? translatedObj[0] : translatedObj[1];
        this.exportActionSuccessText = translatedObj[2];
      });
  }

  focusOnElement() {
    this.utilityBar$()
      .exportButton()
      .nativeElement.focus();
  }

  showMultiActionModal(event: string) {
    this.sharedListMultiActionModal$().show(event, this.sellVehiclesToggled$());
  }

  analyticsTrackPageView(): void {
    const itemIds = this.inventorySearchResponseVehicles$()
      ? this.inventorySearchResponseVehicles$().map(item => item.inventoryItemUuid)
      : [];
    const splitArrays = chunkArrayBySize(itemIds, 1);
    const splitRecs = splitArrays.join(' | ');
    const spacedRecs = splitRecs.replace(/,/g, ' ');

    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData?.page?.category?.site}:Sell:Manage Inventory:${this.filterStatusName}`,
        section: 'Sell',
        site: this.analyticsService.digitalData?.page?.category?.site,
        subsection: 'Manage',
        title: 'Inventory',
        attributes: {
          vehicleList: spacedRecs ? spacedRecs : 'No Vehicles'
        }
      }
    });
  }

  completeOfferActions(event) {
    this.store.dispatch(new ClearAllSelectedVehiclesUuid());
    this.sharedListMultiActionModal$().close();
    this.createBanners(event);
  }

  createBanners(event) {
    this.clearMultiActionBanners();
    this.createMultiActionBanners(event);
    this.updateVehicleStatusFromMultiOfferAction(event);
  }

  clearMultiActionBanners() {
    this.multiOfferActionSuccess = null;
    this.multiOfferActionErrors = [];
  }

  analyticsMessageReference(confirmMessage: string) {
    return this.isInServiceVehiclesEnabled ? 'inService' + confirmMessage : 'cbl' + confirmMessage;
  }

  createMultiActionBanners(multiOfferBanners: MultiOfferActionBannerInfo) {
    this.translocoService
      .selectTranslateObject(multiOfferBanners.translationCodeMapping)
      .pipe(take(1))
      .subscribe(
        translate => {
          if (multiOfferBanners.successPresent) {
            this.multiOfferActionSuccess = {
              text: translate.pop(),
              showErrorIcon: false,
              showIcon: true,
              showUndo: false,
              showSuccessIcon: true,
              mode: 'confirmation'
            };
            this.analyticsService.track('impressionEvent', {
              name: this.analyticsMessageReference('ConfirmationMessageEvent'),
              contents: [
                {
                  name: this.analyticsMessageReference('ConfirmationSuccessMessageEvent'),
                  message: this.multiOfferActionSuccess.text,
                  attributes: {}
                }
              ]
            });
          }
          this.multiOfferActionErrors = translate.map((errorText: string) => {
            this.analyticsService.track('impressionEvent', {
              name: this.analyticsMessageReference('ConfirmationMessageEvent'),
              contents: [
                {
                  name: this.analyticsMessageReference('ConfirmationErrorMessageEvent'),
                  message: errorText,
                  attributes: {}
                }
              ]
            });
            return {
              text: errorText,
              showErrorIcon: true,
              showIcon: true,
              showUndo: false,
              showSuccessIcon: false,
              mode: 'confirmation'
            };
          });
        },
        error => {
          console.log(error);
        }
      );
  }

  updateVehicleStatusFromMultiOfferAction(event: any) {
    if (event.successPresent) {
      this.disableSelectAll = true;
      let newStatusFromAction;
      switch (event.action) {
        case 'ACCEPT':
          newStatusFromAction = InventoryManagementAuctionItemStatus.SOLD;
          break;
        case 'DECLINE':
          newStatusFromAction = InventoryManagementAuctionItemStatus.ACTIVE;
          break;
        case 'COUNTER':
          newStatusFromAction = InventoryManagementAuctionItemStatus.ACTIVE;
          break;
        case 'REVIVE':
          newStatusFromAction = InventoryManagementAuctionItemStatus.ACTIVE;
          break;
        case 'WITHDRAW':
          newStatusFromAction = InventoryManagementAuctionItemStatus.ACTIVE;
          break;
        default:
          newStatusFromAction = event.action;
      }
      const auctionItemUuids = event.auctionItemUuids;
      const multiOfferMap = this.hadMultiOfferActionSuccess$();
      auctionItemUuids.forEach((auctionItemUuid: string) => {
        multiOfferMap.set(auctionItemUuid, newStatusFromAction);
      });
      // we need to create a new map object so the object reference can update the computed signal inventoryManagementVehicles$
      this.hadMultiOfferActionSuccess$.set(new Map(multiOfferMap));
    }
  }

  // for ALL VEHICLES filter, we change the result text to match the selected vehicle's auction item status
  getAllVehiclesResultText(translations: any[]) {
    const vehicleResultTextMap = {
      [InventoryManagementAuctionItemStatus.ACTIVE]: translations[1]?.vehicleTextTranslations?.textActive,
      [InventoryManagementAuctionItemStatus.RESERVED]: translations[1]?.vehicleTextTranslations?.textReserved,
      [InventoryManagementAuctionItemStatus.SOLD]: translations[1]?.vehicleTextTranslations?.textSold,
      [InventoryManagementAuctionItemStatus.HOLD]: translations[1]?.vehicleTextTranslations?.textHold,
      [InventoryManagementAuctionItemStatus.REMOVED]: translations[1]?.vehicleTextTranslations?.textRemoved
    };

    // if we have made no selections on the ALL VEHICLES page
    if (this.inventorySearchCriteria$().filterByStatus === null) {
      return translations[1]?.vehicleTextTranslations.textResult;
    } else {
      return vehicleResultTextMap[this.inventorySearchCriteria$().filterByStatus];
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.store.dispatch(new UpdateVehicleSelectAll(false));
  }

  closeSuccessBanner() {
    this.multiOfferActionSuccess = null;
  }

  closeErrorBanner(index) {
    this.multiOfferActionErrors.splice(index, 1);
  }
}
